import React, { useState } from 'react';

import { Form, Formik, FormikHelpers } from 'formik';
import { useToasts } from 'react-toast-notifications';

import Button from '@components/common/Button/Button';
import Editor from '@components/common/editor/Editor';
import useTranslateLpc from '@components/common/hooks/useTranslateLpc';
import notificationService from '@services/domain/Lpc/NotificationsService';
import { addMessagePotSchema } from '@validations/potSchema';

import css from './AddMessageForm.module.scss';

type AddMessageFormProps = {
    potRef: string;
    onUpdateActivities: () => void;
};

const AddMessageForm = ({ potRef, onUpdateActivities }: AddMessageFormProps) => {
    const { t } = useTranslateLpc(['lpc-pot', 'common']);
    const { addToast } = useToasts();
    const [editorKey, setEditorKey] = useState<number>(0);

    const [isLoading, setIsLoading] = useState(false);

    const getInitialValues = () => {
        return {
            message: '',
        };
    };

    const onSubmit = (values: { message: string }, formikHelpers: FormikHelpers<{ message: string }>) => {
        setIsLoading(true);
        notificationService
            .addMessage(potRef, values)
            .then((res) => res.status === 200 && onUpdateActivities())
            .then(() => {
                formikHelpers.resetForm({ values: { message: '' } });
                setEditorKey(editorKey + 1);
            })
            .catch((err) => err)
            .finally(() => {
                setIsLoading(false);
                addToast(t(`success.ORGANIZER_MESSAGE_SENT`), {
                    appearance: 'success',
                    autoDismiss: true,
                });
            });
    };

    return (
        <div>
            <Formik
                initialValues={getInitialValues()}
                validationSchema={addMessagePotSchema}
                onSubmit={(values, formikHelpers) => {
                    onSubmit(values, formikHelpers);
                }}
            >
                {({ values }) => (
                    <Form className={css.form}>
                        <div className={css.form__input}>
                            <Editor
                                value={values.message}
                                editorName="message"
                                editorImageUploadUrl={'pot'}
                                editorPlaceholder={t('activity.add-message-placeholder')}
                                editorId="message"
                                key={editorKey}
                                customClass={css.form__input__editor}
                            />
                        </div>

                        {/* SUBMIT */}
                        <div className={css.form__submit}>
                            <Button
                                type={'submit'}
                                variant={'primary'}
                                customClass={css.form__submit__cta}
                                isLoading={isLoading}
                                isDisabled={isLoading || values.message === ''}
                            >
                                {t('activity.add-message-cta')}
                            </Button>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default AddMessageForm;
